export interface BatcherRuntime {
  setTimeout: (callback: () => void, ms: number) => unknown
  clearTimeout: (handle: unknown) => void
}

export interface PacketBatcherOptions {
  trailingMs?: number
  maxWaitMs?: number
  maxBatchSize?: number
  runtime?: BatcherRuntime
}

type Sender<Packet> = (packets: readonly Packet[]) => Promise<void>

interface Waiter {
  resolve: () => void
  reject: (error: unknown) => void
}

interface Queue<Packet> {
  packets: Packet[]
  waiters: Waiter[]
  send: Sender<Packet>
  trailingTimer?: unknown
  maxWaitTimer?: unknown
}

export const DEFAULT_TRAILING_MS = 500
export const DEFAULT_MAX_WAIT_MS = 2000
export const DEFAULT_MAX_BATCH_SIZE = 21

const defaultRuntime: BatcherRuntime = {
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
}

/**
 * Groups packets by queue key and sends each group as one request.
 *
 * A queue flushes when it has been idle for `trailingMs`, when `maxWaitMs` has passed since its first packet,
 * or when it holds `maxBatchSize` packets. A flush detaches the queue before sending, so packets that arrive
 * while a request is in flight start a new queue.
 */
export class PacketBatcher<Packet> {
  private readonly queues = new Map<string, Queue<Packet>>()
  private readonly trailingMs: number
  private readonly maxWaitMs: number
  private readonly maxBatchSize: number
  private readonly runtime: BatcherRuntime

  constructor({
    trailingMs = DEFAULT_TRAILING_MS,
    maxWaitMs = DEFAULT_MAX_WAIT_MS,
    maxBatchSize = DEFAULT_MAX_BATCH_SIZE,
    runtime = defaultRuntime,
  }: PacketBatcherOptions = {}) {
    this.trailingMs = trailingMs
    this.maxWaitMs = maxWaitMs
    this.maxBatchSize = maxBatchSize
    this.runtime = runtime
  }

  /** Accepts a packet for a later request. Delivery failures are contained. */
  enqueue(key: string, packet: Packet, send: Sender<Packet>): void {
    const queue = this.add(key, packet, send)
    if (queue.packets.length >= this.maxBatchSize) {
      this.detachAndSend(key)
      return
    }
    this.schedule(key, queue)
  }

  /** Sends the packet now, together with pending packets for the key. Settles with that request. */
  enqueueImmediately(key: string, packet: Packet, send: Sender<Packet>): Promise<void> {
    const queue = this.add(key, packet, send)
    queue.send = send
    const delivery = new Promise<void>((resolve, reject) => {
      queue.waiters.push({ resolve, reject })
    })
    this.detachAndSend(key)
    return delivery
  }

  /** Sends pending packets for one key, or for every key. Does not wait for the requests. */
  flush(key?: string): void {
    const keys = key === undefined ? [...this.queues.keys()] : [key]
    keys.forEach(queueKey => this.detachAndSend(queueKey))
  }

  /** Drops pending packets and timers without sending them. */
  reset(): void {
    this.queues.forEach(queue => this.clearTimers(queue))
    this.queues.clear()
  }

  private add(key: string, packet: Packet, send: Sender<Packet>): Queue<Packet> {
    let queue = this.queues.get(key)
    if (!queue) {
      queue = { packets: [], waiters: [], send }
      this.queues.set(key, queue)
    }
    queue.packets.push(packet)
    return queue
  }

  private schedule(key: string, queue: Queue<Packet>): void {
    if (queue.trailingTimer !== undefined) {
      this.runtime.clearTimeout(queue.trailingTimer)
    }
    queue.trailingTimer = this.runtime.setTimeout(() => this.detachAndSend(key), this.trailingMs)
    if (queue.maxWaitTimer === undefined) {
      queue.maxWaitTimer = this.runtime.setTimeout(() => this.detachAndSend(key), this.maxWaitMs)
    }
  }

  private clearTimers(queue: Queue<Packet>): void {
    if (queue.trailingTimer !== undefined) {
      this.runtime.clearTimeout(queue.trailingTimer)
    }
    if (queue.maxWaitTimer !== undefined) {
      this.runtime.clearTimeout(queue.maxWaitTimer)
    }
  }

  private detachAndSend(key: string): void {
    const queue = this.queues.get(key)
    if (!queue) {
      return
    }
    this.queues.delete(key)
    this.clearTimers(queue)

    let request: Promise<void>
    try {
      request = queue.send(queue.packets)
    } catch (error) {
      request = Promise.reject(error)
    }
    request.then(
      () => queue.waiters.forEach(waiter => waiter.resolve()),
      error => queue.waiters.forEach(waiter => waiter.reject(error))
    )
  }
}
